import React, { useEffect, useCallback, useState } from 'react';
import { 
  Container, 
  Grid,
  Card,
  Text, 
  Group, 
  Title,
  Table,
  Badge, 
  Alert,
  Button,
  Stack, 
  SimpleGrid,
  ThemeIcon,
  Progress,
  Box,
} from '@mantine/core';
// import { PieChart } from '@mantine/charts';
import { 
  IconAlertCircle,
  IconRefresh,
  IconHexagon,
  IconCircle,
  IconSquare,
} from '@tabler/icons-react';
import { useSupabase } from '../contexts/SupabaseContext';
import { getConfig } from '../config/network';
import { isButtonAllowed } from '../utils/permissions';

// Foundation Wallet 상수
const FOUNDATION_WALLETS = [
  {
    name: 'Treasury',
    address: '0xf634f28632e348530538F3E37e4E13252A67A17A',
    balance: '0',
  },
  {
    name: 'Private Sale',
    address: '0xB183093A00D6577400099c7cBAa15cD3437F3BED',
    balance: '0',
  },
  {
    name: 'Liquidity',
    address: '0xcE80026bAd6A41814259F28a86e534FFC1971489',
    balance: '0',
  },
  {
    name: 'Team & Advisors',
    address: '0xB80613E1fd3D9e3C0FC4F8D500aA96fbCD254c96',
    balance: '0',
  },
];

const Dashboard = () => {
  const {
    loadDashboardStats,
    loadActivityLogs,
    loadFoundationWallets,
    isConnected: supabaseConnected,
    error: supabaseError,
    myAdmin
  } = useSupabase();
  const config = getConfig();

  // 상태 관리
  const [stats, setStats] = useState(null);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exchangeStatus, setExchangeStatus] = useState([
    { exchange: 'Coinstore', volume: '$0', amount: '$0', price: '$0', status: 'Loading' },
    { exchange: 'Indodax', volume: '$0', amount: '$0', price: '$0', status: 'Preparing' }
  ]);

  // Coinstore API 호출 함수
  const fetchCoinstoreData = useCallback(async () => {
    try {
      // Price API 호출
      const priceResponse = await fetch('https://api.coinstore.com/api/v1/ticker/price');
      const priceData = await priceResponse.json();
      
      // Volume API 호출
      const volumeResponse = await fetch('https://api.coinstore.com/api/v1/market/tickers');
      const volumeData = await volumeResponse.json();
      
      console.log('Price API Response:', priceData);
      console.log('Volume API Response:', volumeData);
      
      // API 응답 구조 확인 및 데이터 찾기
      let edenaPriceData = null;
      let edenaVolumeData = null;
      
      // Price 데이터 찾기 (응답 구조에 따라 처리)
      if (Array.isArray(priceData)) {
        edenaPriceData = priceData.find(item => item.symbol === 'EDENAUSDT');
      } else if (priceData.data && Array.isArray(priceData.data)) {
        edenaPriceData = priceData.data.find(item => item.symbol === 'EDENAUSDT');
      } else if (priceData.result && Array.isArray(priceData.result)) {
        edenaPriceData = priceData.result.find(item => item.symbol === 'EDENAUSDT');
      }
      
      // Volume 데이터 찾기 (응답 구조에 따라 처리)
      if (Array.isArray(volumeData)) {
        edenaVolumeData = volumeData.find(item => item.symbol === 'EDENAUSDT');
      } else if (volumeData.data && Array.isArray(volumeData.data)) {
        edenaVolumeData = volumeData.data.find(item => item.symbol === 'EDENAUSDT');
      } else if (volumeData.result && Array.isArray(volumeData.result)) {
        edenaVolumeData = volumeData.result.find(item => item.symbol === 'EDENAUSDT');
      }
      
      if (edenaPriceData && edenaVolumeData) {
        const price = parseFloat(edenaPriceData.price);
        const volume = parseFloat(edenaVolumeData.volume);
        const amount = parseFloat(edenaVolumeData.amount || edenaVolumeData.quoteVolume || 0);
        
        setExchangeStatus(prev => prev.map(exchange => {
          if (exchange.exchange === 'Coinstore') {
            return {
              ...exchange,
              price: `$${price.toFixed(4)}`,
              volume: `${volume.toLocaleString()}`,
              amount: `$${(amount / 1000000).toFixed(2)}M`,
              status: 'Active'
            };
          }
          return exchange;
        }));
      } else {
        // EDENAUSDT 데이터를 찾을 수 없는 경우
        setExchangeStatus(prev => prev.map(exchange => {
          if (exchange.exchange === 'Coinstore') {
            return {
              ...exchange,
              price: '$0',
              volume: '$0',
              amount: '$0',
              status: 'No Data'
            };
          }
          return exchange;
        }));
      }
    } catch (error) {
      console.error('Coinstore API 호출 실패:', error);
      setExchangeStatus(prev => prev.map(exchange => {
        if (exchange.exchange === 'Coinstore') {
          return {
            ...exchange,
            price: '$0',
            volume: '$0',
            amount: '$0',
            status: 'Error'
          };
        }
        return exchange;
      }));
    }
  }, []);

  const loadActivityLogsFromSupabase = useCallback(async (currentStats = null) => {
    try {
      if (!supabaseConnected) {
        return;
      }

      const activityLogs = await loadActivityLogs(5);

      const formattedEvents = activityLogs.map((log) => {
        return {
          id: log.id,
          action: log.action,
          account: log.account,
          value: log.value,
          timestamp: log.created_at,
          status: log.status,
          blockNumber: log.block_number,
          transactionHash: log.transaction_hash
        };
      });

      setActivityLogs(formattedEvents);

    } catch (error) {
      setActivityLogs([]);
    }
  }, [supabaseConnected, loadActivityLogs]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      if (!supabaseConnected) {
        console.error('Supabase 연결이 필요합니다.');
        return;
      }

      const supabaseStats = await loadDashboardStats();

      const newStats = {
        foundationBalance: '0',
        lockedAmount: supabaseStats.totalLockedAmount.toLocaleString(),
        lockedAccounts: supabaseStats.activeLockups,
        lockupCount: supabaseStats.totalLockups,
        decimal: config.decimals || 18,
        contractAddress: config.contractAddress,
        foundationWallets: FOUNDATION_WALLETS,
        totalAccounts: supabaseStats.totalAccounts,
        totalSupply: supabaseStats.totalSupply.toLocaleString(),
        totalPreSale: supabaseStats.totalPreSale.toLocaleString(),
        kycStats: supabaseStats.kycStats,
        weeklyUnlocks: supabaseStats.weeklyUnlocks,
      };

      try {
        const foundationWalletsData = await loadFoundationWallets();
        const foundationWallets = [...FOUNDATION_WALLETS];
        const updatedWallets = foundationWallets.map(wallet => {
          const walletData = foundationWalletsData.find(w => w.address === wallet.address);
          return {
            ...wallet,
            balance: walletData ? parseFloat(walletData.balance).toLocaleString() : '0'
          };
        });

        const totalFoundationBalance = updatedWallets.reduce((sum, wallet) => {
          const balance = parseFloat(wallet.balance.replace(/,/g, '')) || 0;
          return sum + balance;
        }, 0);

        newStats.foundationBalance = totalFoundationBalance.toLocaleString();
        newStats.foundationWallets = updatedWallets;
      } catch (foundationError) {
        console.error('Foundation wallets 데이터 로드 실패:', foundationError);
        newStats.foundationBalance = '0';
        newStats.foundationWallets = FOUNDATION_WALLETS;
      }

      setStats(newStats);
      
      // Coinstore API 데이터 로드
      await fetchCoinstoreData();
    } catch (error) {
      console.error('데이터 로드 실패:', error);
    } finally {
      setLoading(false);
    }
  }, [config.contractAddress, config.decimals, supabaseConnected, loadDashboardStats, loadFoundationWallets, fetchCoinstoreData]);

  useEffect(() => {
    if (supabaseConnected && !stats && !loading) {
      loadData();
    }
  }, [supabaseConnected, stats, loading, loadData]);

  useEffect(() => {
    if (stats && supabaseConnected && activityLogs.length === 0) {
      loadActivityLogsFromSupabase(stats);
    }
  }, [stats, supabaseConnected, activityLogs.length, loadActivityLogsFromSupabase]);


  const refreshActivityLogs = useCallback(async () => {
    await loadActivityLogsFromSupabase(stats);
  }, [loadActivityLogsFromSupabase, stats]);

  const refreshExchangeData = useCallback(async () => {
    await fetchCoinstoreData();
  }, [fetchCoinstoreData]);

  const lockedAmount = stats?.lockedAmount || '0';
  const foundationWallets = stats?.foundationWallets || FOUNDATION_WALLETS;

  // 상단 통계 카드 데이터 (디자인 유지, 값은 stats에서 채움)
  const statisticsData = [
    { title: 'Total Supply', value: stats?.totalSupply?.toString() || '-', unit: 'EDENA', icon: IconHexagon, color: 'blue' },
    { title: 'Total Accounts', value: stats?.totalAccounts?.toString() || '-', unit: 'Accounts', icon: IconCircle, color: 'green' },
    { title: 'Locked Amount', value: lockedAmount, unit: 'EDENA', icon: IconSquare, color: 'purple' },
    { title: 'This Week Expire', value: stats?.weeklyUnlocks?.[0]?.value?.toLocaleString() || '0', unit: 'EDENA', icon: IconSquare, color: 'orange' },
  ];

  // exchangeStatus는 이제 상태로 관리됨
  
  // 실제 데이터베이스에서 가져온 주별 언락 데이터 사용
  const lockupData = stats?.weeklyUnlocks || [
    { week: 'Week 1', value: 0 },
    { week: 'Week 2', value: 0 },
    { week: 'Week 3', value: 0 },
    { week: 'Week 4', value: 0 }
  ];

  return (
    <Container fluid py="md" pl="xl">
      <Group justify="space-between" mb="sm">
        <Title order={2}>Dashboard</Title>
        <Button 
          leftSection={<IconRefresh size="0.9rem" />} 
          variant="light" 
          onClick={loadData} 
          loading={loading} 
          size="sm"
          disabled={!isButtonAllowed(myAdmin, 'refresh')}
        >
          Refresh
        </Button>
              </Group>

      {supabaseError && (
        <Alert icon={<IconAlertCircle size="1rem" />} color="red" radius="md" mb="md">Database 연결 오류: {supabaseError}. 데이터를 불러올 수 없습니다.</Alert>
      )}

      {/* 상단 통계 카드 (디자인 유지) */}
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} mb="lg" spacing="sm">
          {statisticsData.map((stat, index) => (
            <Card key={index} withBorder shadow="sm" p="sm" radius="md">
              <Group justify="space-between" mb="xs">
                <ThemeIcon color={stat.color} variant="light" size="lg">
                  <stat.icon size={20} />
                </ThemeIcon>
              </Group>
            <Text size="sm" c="dimmed" mb={4}>{stat.title}</Text>
            <Text size="xl" fw={700} mb={2}>{stat.value}</Text>
            <Text size="sm" c="dimmed">{stat.unit}</Text>
            </Card>
          ))}
        </SimpleGrid>

      {/* 중간 섹션 (디자인 유지) */}
        <Grid mb="lg" gutter="sm">
          {/* Lockup Summary */}
          <Grid.Col span={{ base: 12, md: 4 }}>
            <Card withBorder shadow="sm" p="sm" h="100%">
              <Title order={4} mb="xs">Lockup Summary</Title>
              <Text size="sm" c="dimmed" mb="sm">Weekly unlock schedule</Text>
              <Stack gap="xs">
                {lockupData.map((item, index) => (
                  <Box key={index}>
                    <Group justify="space-between" mb="xs">
                      <Text size="sm">{item.week}</Text>
                      <Text size="sm" fw={500}>{item.value.toLocaleString()}</Text>
                    </Group>
                    <Progress 
                      value={(() => {
                        // 총 락업 금액을 기준으로 비율 계산
                        const totalLocked = parseFloat(stats?.lockedAmount?.replace(/,/g, '') || 0);
                        if (totalLocked === 0) return 0;
                        return Math.min((item.value / totalLocked) * 100, 100);
                      })()} 
                      color="purple" 
                      size="sm" 
                    />
                  </Box>
                ))}
              </Stack>
            </Card>
          </Grid.Col>

          {/* Foundation Wallets */}
          <Grid.Col span={{ base: 12, md: 4 }}>
            <Card withBorder shadow="sm" p="sm" h="100%">
              <Title order={4} mb="xs">Foundation Wallets</Title>
              <Text size="sm" c="dimmed" mb="sm">Token distribution by category</Text>
              <Text size="sm" mb="sm"> </Text>
              <Stack gap="md">
                {foundationWallets.map((wallet, index) => (
                  <Group key={index} justify="space-between">
                  <Text size="sm">{wallet.name}</Text>
                    <Group>
                    <Text size="sm" fw={500}>{wallet.balance}</Text>
                    {/* <Text size="sm" c="dimmed">({wallet.address.slice(0,6)}...{wallet.address.slice(-4)})</Text> */}
                    <Text size="sm" c="dimmed">({wallet.address.slice(0,16)}...{wallet.address.slice(-4)})</Text>
                    </Group>
                  </Group>
                ))}
              </Stack>
            </Card>
          </Grid.Col>

          {/* Exchange Status */}
          <Grid.Col span={{ base: 12, md: 4 }}>
            <Card withBorder shadow="sm" p="sm" h="100%">
              <Group justify="space-between" mb="xs">
                <Title order={4}>Exchange Status</Title>
                <Button 
                  leftSection={<IconRefresh size="0.9rem" />} 
                  variant="light" 
                  onClick={refreshExchangeData} 
                  size="xs"
                  disabled={!isButtonAllowed(myAdmin, 'refresh')}
                >
                  Refresh
                </Button>
              </Group>
              <Text size="sm" c="dimmed" mb="sm">Current market data</Text>
              <Stack gap="sm">
                {exchangeStatus.map((exchange, index) => {
                  const getStatusColor = (status) => {
                    switch (status) {
                      case 'Active': return 'green';
                      case 'Loading': return 'blue';
                      case 'Error': return 'red';
                      case 'No Data': return 'yellow';
                      case 'Preparing': return 'gray';
                      default: return 'gray';
                    }
                  };
                  
                  return (
                    <Box key={index} p="sm" bg="gray.0" style={{ borderRadius: '8px' }}>
                      <Group justify="space-between" mb="xs">
                        <Text size="sm" fw={500}>{exchange.exchange}</Text>
                        <Badge color={getStatusColor(exchange.status)} size="sm">{exchange.status}</Badge>
                      </Group>
                      <Group justify="space-between">
                        <Text size="xs" c="dimmed">Volume: {exchange.volume}</Text>
                        <Text size="xs" c="dimmed">Amount: {exchange.amount}</Text>
                        <Text size="xs" c="dimmed">Price: {exchange.price}</Text>
                      </Group>
                    </Box>
                  );
                })}
              </Stack>
            </Card>
          </Grid.Col>
        </Grid>

      {/* 하단 섹션 (디자인 유지) */}
        <Grid gutter="sm">
          {/* Recent Activity */}
          <Grid.Col span={{ base: 12, md: 8 }}>
            <Card withBorder shadow="sm" p="sm">
            <Group justify="space-between" mb="xs">
              <Title order={4}>Recent Activity</Title>
              <Button 
                leftSection={<IconRefresh size="0.9rem" />} 
                variant="light" 
                onClick={refreshActivityLogs} 
                size="xs"
                disabled={!isButtonAllowed(myAdmin, 'refresh')}
              >
                Refresh Logs
              </Button>
            </Group>
              <Text size="sm" c="dimmed" mb="sm">Latest token transfers</Text>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                  <Table.Th>Transaction</Table.Th>
                  <Table.Th>Account</Table.Th>
                  <Table.Th>Value</Table.Th>
                  <Table.Th style={{ minWidth: 120, whiteSpace: 'nowrap' }}>Block #</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                {activityLogs.map((log) => (
                  <Table.Tr key={log.id}>
                    <Table.Td style={{ maxWidth: 260, overflow: 'hidden' }}>
                      <Text size="xs" ff="monospace" c="blue" style={{ lineHeight: 1.2, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                        {log.transactionHash || 'N/A'}
                      </Text>
                    </Table.Td>
                    <Table.Td style={{ maxWidth: 260, overflow: 'hidden' }}>
                      <Text size="xs" ff="monospace" style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                        {log.account}
                      </Text>
                    </Table.Td>
                    <Table.Td><Text size="xs" c="blue" fw={600}>{log.value || 'N/A'}</Text></Table.Td>
                    <Table.Td style={{ minWidth: 120, whiteSpace: 'nowrap' }}><Text size="xs" c="dimmed">{log.blockNumber}</Text></Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Card>
          </Grid.Col>

          {/* KYC Status */}
          <Grid.Col span={{ base: 12, md: 4 }}>
            <Card withBorder shadow="sm" p="sm">
              <Title order={4} mb="xs">KYC Status</Title>
              <Text size="sm" c="dimmed" mb="sm">Verification status breakdown</Text>
              {stats?.kycStats ? (
                <Group align="center" justify="space-between" gap="lg">
                  {/* 파이차트 */}
                  <Box
                    style={{
                      width: 200,
                      height: 200,
                      borderRadius: '50%',
                      background: `conic-gradient(
                        #51cf66 0deg ${(stats.kycStats.verified / (stats.kycStats.verified + stats.kycStats.anonymous)) * 360}deg,
                        #ff6b6b ${(stats.kycStats.verified / (stats.kycStats.verified + stats.kycStats.anonymous)) * 360}deg 360deg
                      )`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                      flexShrink: 0
                    }}
                  >
                    <Box
                      style={{
                        width: 120,
                        height: 120,
                        borderRadius: '50%',
                        backgroundColor: 'var(--mantine-color-body)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Text size="lg" fw={600}>
                        {stats.kycStats.verified + stats.kycStats.anonymous}
                      </Text>
                    </Box>
                  </Box>
                  
                  {/* 범례 */}
                  <Stack gap="md" justify="center" style={{ height: 200 }}>
                    <Group>
                      <Box w={12} h={12} bg="green" style={{ borderRadius: '50%' }} />
                      <Text size="sm">Verified: {stats.kycStats.verified}</Text>
                    </Group>
                    <Group>
                      <Box w={12} h={12} bg="red" style={{ borderRadius: '50%' }} />
                      <Text size="sm">Anonymous: {stats.kycStats.anonymous}</Text>
                    </Group>
                  </Stack>
                </Group>
              ) : (
                <Box ta="center" py="xl">
                  <Text size="sm" c="dimmed">KYC 상태 데이터가 없습니다</Text>
                </Box>
              )}
            </Card>
          </Grid.Col>
        </Grid>
      </Container>
  );
};

export default Dashboard;
